import type { PaperWithRole } from "@/types/paper";

export type TabId = "Frame" | "Curate" | "Analyze" | "Build";
export type PhaseId = "Curate" | "Analyze" | "Build";
export type FunnelTier = "corpus" | "scored" | "read" | "evidence";
export type PhaseState = "active" | "done" | "default";

export interface FunnelCounts {
  /** Total trawled papers, including dismissed (the display total). */
  corpus: number;
  /** Trawled papers still active (not excluded/dismissed) — the frontier denominator. */
  activeCorpus: number;
  scored: number;
  read: number;
  /** Active papers at exactly 'skimmed' — rendered as a lighter segment on the Read bar. */
  skimmed: number;
  evidence: number;
}

/** Pathname → workflow tab. Mirrors the mapping in __root.tsx. */
export function pathToTab(pathname: string): TabId {
  if (pathname.includes("/analyze")) return "Analyze";
  if (pathname.includes("/evidence") || pathname.includes("/build")) return "Build";
  if (pathname.includes("/expand") || pathname.includes("/curate")) return "Curate";
  return "Frame";
}

/** Active = not excluded and not manually scored down to the dismiss floor. */
function isActive(p: PaperWithRole): boolean {
  return (
    !p.exclusion_reason &&
    !(p.user_score_override != null && p.user_score_override <= 30)
  );
}

/** Corpus lifecycle counts. Cumulative (corpus is the total; the rest are subsets). */
export function funnelCounts(
  papers: PaperWithRole[],
  claimCount: number | null,
): FunnelCounts {
  return {
    corpus: papers.length,
    activeCorpus: papers.filter(isActive).length,
    scored: papers.filter((p) => p.scoring_timestamp != null && isActive(p)).length,
    read: papers.filter((p) => p.reading_status === "read" && isActive(p)).length,
    skimmed: papers.filter((p) => p.reading_status === "skimmed" && isActive(p)).length,
    evidence: claimCount ?? 0,
  };
}

/**
 * The next actionable tier to ring in the nest, or null when the lifecycle is
 * complete. Compares against the ACTIVE corpus (excluding dismissed papers) so
 * the frontier advances past "scored" once every active paper is scored — even
 * when dismissed papers keep the display total higher than the scored count.
 */
export function computeFunnelFrontier(c: FunnelCounts): FunnelTier | null {
  const active = c.activeCorpus ?? c.corpus;
  if (c.corpus === 0 || active === 0) return "corpus";
  if (c.scored < active) return "scored";
  if (c.read === 0) return "read";
  if (c.evidence === 0) return "evidence";
  return null;
}

const PHASE_ORDER: PhaseId[] = ["Curate", "Analyze", "Build"];

/** Earlier-than-active phases read as "done" (passed); the current one is "active". */
export function computePhaseStates(tab: TabId): Record<PhaseId, PhaseState> {
  const activeIdx = PHASE_ORDER.indexOf(tab as PhaseId);
  const out = {} as Record<PhaseId, PhaseState>;
  PHASE_ORDER.forEach((phase, i) => {
    out[phase] =
      activeIdx === -1 ? "default" : i < activeIdx ? "done" : i === activeIdx ? "active" : "default";
  });
  return out;
}

/**
 * Curate-filtering tiers → /curate search params (legacy handleStageClick map).
 * Every tier filters the Curate table in place — "evidence" via the per-paper
 * has_evidence flag (papers the user attached ClaimEvidence to). Evidence
 * *work* still lives in Build; this just shows which papers made it there.
 */
export const STAGE_SEARCH: Record<FunnelTier, Record<string, unknown>> = {
  corpus: {},
  scored: { minScore: 1 },
  read: { readingStatus: "read" },
  evidence: { hasEvidence: true },
};
